import {
  ActivityIndicator,
  Image,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { ArrowRight } from "lucide-react-native";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type CardField = {
  label: string;
  value: string;
};

type Props = {
  category: string;
  status: string;
  title: string;
  subtitle: string;
  fields: [CardField, CardField];
  actionLabel?: string;
  onAction?: () => void;
  loading?: boolean;
};

// This is the same silk background and glass treatment used by the homepage
// and Care medical-history card. It is kept as a comparison variant until a
// direction is selected for the real Coverage & Benefits surface.
const silkBrown =
  require("@src/apps/moon/assets/images/silk-brown.png") as number;

export default function TexturedCoverageCard({
  category,
  status,
  title,
  subtitle,
  fields,
  actionLabel,
  onAction,
  loading = false,
}: Props) {
  const capturePress = usePressCapture("TexturedCoverageCard");
  return (
    <View
      style={{
        borderRadius: 24,
        overflow: "hidden",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.12,
        shadowRadius: 24,
      }}
    >
      <Image
        source={silkBrown}
        resizeMode="cover"
        style={[StyleSheet.absoluteFill, { width: "100%", height: "100%" }]}
      />
      <View
        style={{
          minHeight: 244,
          padding: 17,
          backgroundColor: "rgba(255,255,255,0.12)",
          borderRadius: 24,
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.18)",
          ...(Platform.OS === "web"
            ? ({
                backdropFilter: "blur(40px)",
                WebkitBackdropFilter: "blur(40px)",
              } as Record<string, string>)
            : {}),
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              className="font-dm-sans"
              numberOfLines={1}
              style={{
                fontSize: 19,
                lineHeight: 24,
                fontWeight: "700",
                letterSpacing: -0.3,
                color: "#FFFFFF",
              }}
            >
              {category}
            </Text>
            <Text
              className="font-dm-sans"
              numberOfLines={1}
              style={{
                marginTop: 4,
                fontSize: 11.5,
                lineHeight: 16,
                fontWeight: "500",
                color: "rgba(255,255,255,0.66)",
              }}
            >
              {subtitle}
            </Text>
          </View>
          <View
            style={{
              borderRadius: 999,
              paddingHorizontal: 9,
              paddingVertical: 4,
              backgroundColor: "rgba(255,255,255,0.08)",
            }}
          >
            {loading ? (
              <ActivityIndicator
                accessibilityLabel={status}
                size="small"
                color="rgba(255,255,255,0.82)"
              />
            ) : (
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  fontSize: 10,
                  lineHeight: 13,
                  fontWeight: "600",
                  color: "rgba(255,255,255,0.75)",
                }}
              >
                {status}
              </Text>
            )}
          </View>
        </View>

        <Text
          className="font-dm-sans"
          numberOfLines={2}
          style={{
            marginTop: 22,
            fontSize: 21,
            lineHeight: 27,
            fontWeight: "700",
            letterSpacing: -0.3,
            color: "#FFFFFF",
          }}
        >
          {title}
        </Text>

        <View style={{ flexDirection: "row", gap: 20, marginTop: 18 }}>
          {fields.map((field) => (
            <View key={field.label} style={{ flex: 1, minWidth: 0 }}>
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  fontSize: 10,
                  lineHeight: 13,
                  fontWeight: "600",
                  color: "rgba(255,255,255,0.55)",
                  letterSpacing: 0.35,
                  textTransform: "uppercase",
                }}
              >
                {field.label}
              </Text>
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  marginTop: 4,
                  fontSize: 14,
                  lineHeight: 18,
                  fontWeight: "600",
                  color: "#FFFFFF",
                }}
              >
                {field.value}
              </Text>
            </View>
          ))}
        </View>

        {actionLabel ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={actionLabel}
            disabled={!onAction}
            onPress={
              // Keep the handler undefined when the caller passed none.
              onAction
                ? () => {
                    // `actionLabel` is translated copy supplied by the caller.
                    capturePress(
                      pressLabel(actionLabel),
                      "textured_coverage_card_action",
                    );
                    onAction();
                  }
                : undefined
            }
            style={({ pressed }) => ({
              marginTop: "auto",
              opacity: !onAction ? 0.45 : pressed ? 0.78 : 1,
            })}
          >
            <LinearGradient
              colors={["rgba(255,255,255,0.12)", "rgba(255,255,255,0.06)"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 0, y: 1 }}
              style={{
                paddingVertical: 12,
                paddingHorizontal: 20,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                borderWidth: 1,
                borderColor: "rgba(255,255,255,0.1)",
                borderRadius: 14,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  lineHeight: 18,
                  fontWeight: "500",
                  color: "#FFFFFF",
                  letterSpacing: 0.3,
                }}
              >
                {actionLabel}
              </Text>
              <ArrowRight size={15} color="#FFFFFF" strokeWidth={2} />
            </LinearGradient>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}
